import { PageContainer } from '@ant-design/pro-layout'
import { makeStyles } from '@material-ui/styles'
import { Button, Card, Modal, Space, Table, Typography as Text, Tag } from 'antd'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import InvoiceSearchPanel from '@/components/InvoiceSearchPanel'
import config from '@/config'
import dimorderApi from '@/libs/api/dimorderApi'
import { useSelector } from '@/redux'
import colors from '@/style/colors'

const getTodayRange = () => {
  const start = moment().startOf('day').seconds(0).milliseconds(0)
  const end = moment().endOf('day').seconds(0).milliseconds(0)
  return {
    startedAt: start.toISOString(),
    endedAt: end.toISOString(),
  }
}

export default function EinvoiceInvoices() {
  const { t, i18n } = useTranslation()
  const classes = useStyles()
  const activeMerchant = useSelector((state) => state.merchant.activeMerchant)
  const receiptUrl = config.receipt
  const defaultDateRange = React.useMemo(() => getTodayRange(), [])

  const [filters, setFilters] = React.useState({
    status: 'issued',
    orderSerial: undefined,
    invoiceNo: undefined,
    limit: 20,
    startedAt: defaultDateRange.startedAt,
    endedAt: defaultDateRange.endedAt,
    deviceId: undefined,
  })
  const [loading, setLoading] = React.useState(false)
  const [items, setItems] = React.useState([])
  const [currentPage, setCurrentPage] = React.useState(1)
  const [nextCursor, setNextCursor] = React.useState(undefined)
  const [pageCache, setPageCache] = React.useState({})
  const [totalCount, setTotalCount] = React.useState(undefined)
  const [totalAmount, setTotalAmount] = React.useState(undefined)

  const fetchInvoices = async (override = {}, targetPage = 1, pageCursor = undefined) => {
    const params = { ...filters, ...override }
    setLoading(true)
    try {
      const response = await dimorderApi.instance(activeMerchant?.id)?.getEinvoiceInvoices(params)
      const list = Array.isArray(response) ? response : response?.items || response?.invoices || []
      const fetchedNextCursor = response?.cursor

      setItems(list)
      setNextCursor(fetchedNextCursor)
      if (response?.count != null) setTotalCount(response.count)
      if (response?.amount != null) setTotalAmount(response.amount)
      setPageCache((prev) => ({
        ...prev,
        [targetPage]: {
          items: list,
          nextCursor: fetchedNextCursor,
          cursor: pageCursor,
        },
      }))
    } catch (error) {
      console.error(error)
      Modal.error({
        title: t('app.common.error'),
        content: t('app.common.submitError'),
      })
    }
    setLoading(false)
  }

  const onSearch = () => {
    // reset cursor when new search
    setCurrentPage(1)
    setNextCursor(undefined)
    setPageCache({})
    setTotalCount(undefined)
    setTotalAmount(undefined)
    fetchInvoices({ cursor: undefined }, 1, undefined)
  }

  const onNextPage = () => {
    const targetPage = currentPage + 1
    const cachedPage = pageCache[targetPage]

    if (cachedPage) {
      setItems(cachedPage.items)
      setNextCursor(cachedPage.nextCursor)
      setCurrentPage(targetPage)
      return
    }

    if (!nextCursor) return

    fetchInvoices({ cursor: nextCursor }, targetPage, nextCursor)
    setCurrentPage(targetPage)
  }

  const onPrevPage = () => {
    if (currentPage > 1) {
      const targetPage = currentPage - 1
      const cachedPage = pageCache[targetPage]

      if (cachedPage) {
        setItems(cachedPage.items)
        setNextCursor(cachedPage.nextCursor)
      }

      setCurrentPage(targetPage)
    }
  }

  const onReset = () => {
    setFilters({
      status: 'issued',
      orderSerial: undefined,
      invoiceNo: undefined,
      limit: 20,
      startedAt: defaultDateRange.startedAt,
      endedAt: defaultDateRange.endedAt,
    })
    setItems([])
    setCurrentPage(1)
    setNextCursor(undefined)
    setPageCache({})
    setTotalCount(undefined)
    setTotalAmount(undefined)
  }

  const hasNextPage = Boolean(nextCursor || pageCache[currentPage + 1])

  const formatLocalDateTime = (value) => {
    if (!value) return '-'

    if (typeof value === 'object') {
      const seconds = value.seconds ?? value._seconds
      const nanos = value.nanos ?? value._nanoseconds ?? 0
      if (typeof seconds === 'number') {
        const millis = seconds * 1000 + Math.floor(nanos / 1000000)
        return moment(new Date(millis)).format('YYYY/MM/DD HH:mm')
      }
    }

    const m = moment(value)
    return m.isValid() ? m.format('YYYY/MM/DD HH:mm') : '-'
  }

  const getStatusTag = (status) => {
    const statusMap = {
      pending: { color: 'default', label: t('page.einvoiceInvoices.statusPending') },
      issued: { color: 'success', label: t('page.einvoiceInvoices.statusIssued') },
      voided: { color: 'red', label: t('page.einvoiceInvoices.statusVoided') },
      refunded: { color: 'warning', label: t('page.einvoiceInvoices.statusRefunded') },
    }

    return statusMap[status] || { color: 'default', label: status || '-' }
  }

  const carrierTypeLabel = (code) => {
    if (!code) return '-'
    const key = `page.einvoiceInvoices.carrierTypes.${code}`
    const label = t(key)
    return label === key ? t('page.einvoiceInvoices.carrierTypes.other') : label
  }

  const columns = [
    {
      title: t('page.einvoiceInvoices.columns.orderSerial'),
      width: 140,
      dataIndex: 'orderSerial',
      render: (v, record) => {
        if (!v) return '-'
        if (!record?.orderId) return v

        return (
          <Button
            className={classes.btn}
            onClick={() => window.open(receiptUrl + `${record.orderId}?lang=${i18n.language}`)}
          >
            {v}
          </Button>
        )
      },
    },
    {
      title: t('page.einvoiceInvoices.columns.invoiceNo'),
      dataIndex: 'invoiceNo',
      width: 120,
      render: (_, record) => {
        const invoice = `${record?.track || ''}${record?.invoiceNo || ''}`
        return invoice || '-'
      },
    },
    {
      title: t('page.einvoiceInvoices.columns.status'),
      dataIndex: 'status',
      width: 100,
      render: (v) => {
        const { color, label } = getStatusTag(v)
        return <Tag color={color}>{label}</Tag>
      },
    },
    {
      title: t('page.einvoiceInvoices.columns.terminalId'),
      dataIndex: 'terminalId',
      width: 100,
      render: (v) => (typeof v === 'object' ? v?.value : v) || '-',
    },
    {
      title: t('page.einvoiceInvoices.columns.carrierType'),
      dataIndex: 'carrierType',
      width: 130,
      render: (v) => carrierTypeLabel(v),
    },
    {
      title: t('page.einvoiceInvoices.columns.carrierId'),
      dataIndex: 'carrierId',
      width: 150,
      render: (v) => (typeof v === 'object' ? v?.value : v) || '-',
    },
    {
      title: t('page.einvoiceInvoices.columns.donationCode'),
      dataIndex: 'donationCode',
      width: 110,
      render: (v) => (typeof v === 'object' ? v?.value : v) || '-',
    },
    {
      title: t('page.einvoiceInvoices.columns.buyerTaxId'),
      dataIndex: 'buyerTaxId',
      width: 100,
      render: (v) => (typeof v === 'object' ? v?.value : v) || '-',
    },
    {
      title: t('page.einvoiceInvoices.columns.amount'),
      dataIndex: 'totalAmount',
      width: 120,
      render: (v) => (v != null ? `$${Number(v).toLocaleString()}` : '-'),
    },
    {
      title: t('page.einvoiceInvoices.columns.issuedAt'),
      dataIndex: 'issuedAt',
      width: 160,
      render: (v) => formatLocalDateTime(v),
    },
    {
      title: t('page.einvoiceInvoices.columns.voidedAt'),
      dataIndex: 'voidedAt',
      width: 160,
      render: (v) => formatLocalDateTime(v),
    },
    {
      title: t('page.einvoiceInvoices.columns.refundedAt'),
      dataIndex: 'refundedAt',
      width: 160,
      render: (v) => formatLocalDateTime(v),
    },
  ]

  return (
    <PageContainer ghost header={{ title: t('page.einvoiceInvoices.title') }}>
      <InvoiceSearchPanel
        filters={filters}
        onChange={(patch) => setFilters((f) => ({ ...f, ...patch }))}
        onSearch={onSearch}
        onReset={onReset}
        totalCount={totalCount}
        totalAmount={totalAmount}
      />

      <Card>
        <Table
          loading={loading}
          rowKey={(r, idx) => r.id || r.invoiceNo || r.orderSerial || idx}
          columns={columns}
          dataSource={items}
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
        <Space style={{ marginTop: 12 }}>
          <Button disabled={currentPage <= 1} onClick={onPrevPage}>
            {t('app.common.previousPage')}
          </Button>
          <Text>{currentPage}</Text>
          <Button disabled={!hasNextPage} onClick={onNextPage}>
            {t('app.common.nextPage')}
          </Button>
        </Space>
      </Card>
    </PageContainer>
  )
}

const useStyles = makeStyles(() => ({
  btn: {
    borderColor: colors.button,
    borderRadius: 5,
    color: colors.button,
  },
}))
